import * as Sentry from '@sentry/react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import { useIntl } from 'react-intl'
import { accountApi } from '@/api/accountApi'
import { getApiError } from '@/api/helpers/apiError'
import { setCsrfToken } from '@/api/vpApi'
import { PlatformLoader } from '@/components/ui-shared/PlatformLoader/PlatformLoader'
import { showToast } from '@/theme/notifications'
import { PermissionsMap } from '@/types/permissions'
import { Subscription } from '@/types/subscription'
import { User } from '@/types/user'
import { WorkspaceMember } from '@/types/workspace'
import { UserSession, getUserSession } from './helpers'

type LogoutState = {
  logoutType: 'user_logout' | 'initial_auth_check_failed' | 'session_expired'
  redirectToAfterLogin?: string
}

export type SessionContextProps = {
  isAuthenticated: boolean
  user: User | null
  permissions: PermissionsMap
  activeMembership: WorkspaceMember | null
  workspaceMemberships: WorkspaceMember[]
  subscriptions: Subscription[]
  isRefreshing: boolean
  isPasswordExpired: boolean
  logoutState: LogoutState | null
  clearSession: (logoutState?: LogoutState) => void
  refreshSession: () => Promise<void>
}

const initialValues = {
  isAuthenticated: false,
  user: null,
  permissions: {},
  activeMembership: null,
  workspaceMemberships: [],
  subscriptions: [],
  isRefreshing: false,
  isPasswordExpired: false,
  logoutState: null,
  clearSession: () => {},
  refreshSession: async () => {}
}

const SessionContext = createContext<SessionContextProps>(initialValues)

type SessionProviderProps = {
  children: ReactNode
}

export const SessionProvider = ({ children }: SessionProviderProps) => {
  const intl = useIntl()
  const queryClient = useQueryClient()

  const [userSession, setUserSession] = useState<UserSession | null>(null)
  const [isReady, setIsReady] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [logoutState, setLogoutState] = useState<LogoutState | null>(null)
  const [isPasswordExpired, setIsPasswordExpired] = useState(false)

  const { mutateAsync: getCsrfToken } = useMutation({
    mutationFn: () => accountApi.getCsrfToken(),
    retry: 5 // Retry up to 5 times with exponential backoff: 2s, 4s, 8s, 16s, 30s
    // This is the first call when the platform is loading, so we don't want to error out too soon, for example, when redeployment is happening.
  })

  const activeMembership = useMemo(() => {
    return (
      userSession?.workspaceMemberships.find(
        (membership) => membership.is_selected
      ) || null
    )
  }, [userSession])

  const clearSession = useCallback(
    (logoutState?: LogoutState) => {
      setUserSession(null)
      setIsPasswordExpired(false)
      setLogoutState(logoutState || null)
      Sentry.setUser(null)
      queryClient.clear()
    },
    [queryClient]
  )

  const initUserSession = useCallback(async () => {
    setLogoutState(null)
    setIsPasswordExpired(false)

    try {
      // new csrf token must be retrieved after login
      const csrfToken = await getCsrfToken()
      setCsrfToken(csrfToken)
    } catch (err) {
      clearSession({ logoutType: 'session_expired' })
      Sentry.captureException(err)
      return
    }

    try {
      const session = await getUserSession()
      setUserSession(session)

      // TODO: Find a simple way to hash the user ID before sending it to Sentry to keep user information private
      // Sentry.setUser({
      //   id: session.user.id
      // })
    } catch (err) {
      const { status, axiosError } = getApiError(err)
      const responseCode = (
        axiosError?.response?.data as { code?: string } | undefined
      )?.code

      if (status === 403 && responseCode === 'password_expired') {
        setIsPasswordExpired(true)
        return
      }

      // 403 is returned when the user's session has expired, so we don't want to show an error message in this case
      if (status !== 403) {
        Sentry.captureException(err)
        showToast(intl.formatMessage({ id: 'auth.session.error' }), 'error')
      }

      // If a session exists but auth fails, the session has expired.
      // Use 'session_expired' so that after login the user is sent to the root page.

      // If no session exists and auth fails, the user never had a session.
      // Use 'initial_auth_check_failed' so that after login the user is sent back
      // to the page they tried to access (handled in PrivateRoute).
      clearSession(
        userSession
          ? { logoutType: 'session_expired' }
          : {
              logoutType: 'initial_auth_check_failed'
            }
      )
    }
  }, [userSession, intl, clearSession, getCsrfToken])

  useEffect(() => {
    const initOnMount = async () => {
      await initUserSession()
      setIsReady(true)
    }

    void initOnMount()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const refreshSession = useCallback(async () => {
    setIsRefreshing(true)
    await initUserSession()
    setIsRefreshing(false)
  }, [initUserSession])

  const session = useMemo(
    () => ({
      isAuthenticated: Boolean(userSession),
      user: userSession?.user || null,
      permissions: userSession?.permissions || {},
      workspaceMemberships: userSession?.workspaceMemberships || [],
      activeMembership,
      subscriptions: userSession?.subscriptions || [],
      isRefreshing,
      isPasswordExpired,
      logoutState,
      clearSession,
      refreshSession
    }),
    [
      userSession,
      activeMembership,
      isRefreshing,
      isPasswordExpired,
      logoutState,
      clearSession,
      refreshSession
    ]
  )

  return (
    <SessionContext.Provider value={session}>
      {isReady ? children : <PlatformLoader />}
    </SessionContext.Provider>
  )
}

// eslint-disable-next-line react-refresh/only-export-components
export const useSession = () => {
  return useContext(SessionContext)
}
